import React, { useState } from 'react';
import { ListGroup, Form, DropdownButton, Dropdown, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PatientDischargeTaskIcon, UnreadMessageIconIcon, ReminderBellIcon, PatientReportTaskIcon, DeviceControlTaskIcon, FrequencyChangedTaskIcon, BillingTaskIcon, NoteReviewTaskIcon, TranscriptTaskIcon, VitalTaskIcon } from '../../components/icons/Icons';
import { setReloadTaskList, setSelectAll, setSelectedItem, setSelectedRows, toggleItem } from './TasksSlice';
import taskStyles from './Tasks.module.css';
import { formatDateWithFullMonthAndTime, formatDateString, formatDateWithTime } from '../../utils/dateUtils';
import PerinHealthModal from '../../components/PerinHealthModel';
import { closeModal, openModal } from '../../components/PerinHealthModelSlice';
import { useNavigate } from 'react-router-dom';
import { showAlert } from '../../components/PerinHealthAlertSlice';
import useAxios from '../../services/AxiosConfig';
import { setTaskNonCriticalReportId, updatePatientInfo, setTaskEnrollmentId, setFormField } from '../../slicers/PatientSlice';
import DateTimePicker from './DateTimePicker';
import { resetFilterState } from '../patient/Slices/PatientDetailFilterSlice';
import AppointmentTranscription from '../appointment/AppointmentTranscription';
import AdhocModal from './components/AdhocModal';

const TaskListPanel = () => {
  const navigate = useNavigate();
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);
  const [showAdhoc, setShowAdhoc] = useState(false);
  const [adhocDetails, setAdhocDetails] = useState(null);
  const [showTranscription, setShowTranscription] = useState(false);
  const [appointment, setAppointment] = useState(false);
  const dispatch = useDispatch();
  const {
    dataList,
    searchText,
    startDate,
    endDate,
    sortCriteria,
    startDateTimestamp,
    endDateTimestamp,
    selectedTaskTypes,
    selectedRows,
    selectedItem,
    filter,
    sortOrder
  } = useSelector((state) => state.taskListPanel);
  const axiosConfig = useSelector((state) => state.axiosConfig);
  const axios = useAxios(axiosConfig.envURL);
  const userDateFormat = useSelector((state) => state.userInfo.dateFormat);


  const filteredTasks = dataList?.filter(task => {
    const matchesSearch = (task?.description?.toLowerCase()?.includes(searchText?.toLowerCase() || '') ||
      task?.type?.toLowerCase().includes(searchText?.toLowerCase() || ''));
    const taskDate = new Date(task?.createdDate);
    const matchesDateRange = (
      (!startDate || taskDate >= new Date(startDateTimestamp)) &&
      (!endDate || taskDate <= new Date(endDateTimestamp))
    );

    const matchesTaskType = selectedTaskTypes?.length === 0 ||
      selectedTaskTypes?.some(type => type.label === task.type);

    const matchStatus = task?.status === filter?.value;

    return matchesSearch && matchesDateRange && matchesTaskType && matchStatus;
  })?.sort((a, b) => {
    if (sortCriteria.includes("date")) {
      const dateA = new Date(sortCriteria.includes("completed") ? a.completedDate : a.createdDate);
      const dateB = new Date(sortCriteria.includes("completed") ? b.completedDate : b.createdDate);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    } else if (sortCriteria === 'name') {
      const nameA = a?.description?.toLowerCase();
      const nameB = b?.description?.toLowerCase();
      if (nameA < nameB) return sortOrder === "asc" ? -1 : 1;
      if (nameA > nameB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    } else {
      if (a.newStatus && !b.newStatus) return -1;
      if (!a.newStatus && b.newStatus) return 1;

      const dateA = new Date(a.dueDate !== "" ? a.dueDate : a.createdDate);
      const dateB = new Date(b.dueDate !== "" ? b.dueDate : b.createdDate);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    }
  });

  const handleItemChange = (staffTaskId) => {
    dispatch(toggleItem(staffTaskId));
  };

  const handleItemClick = (event, task) => {
    dispatch(setSelectedItem(task));
    if (event.target.textContent === 'Pending') {

    } else if (event.target.textContent === 'Move back to Pending List') {
      dispatch(
        openModal({
          activeModalId: "TaskListPanel",
          header: 'Move Task to pending list?',
          message: "Task will no longer be considered complete and will move back to the Pending Tasks list.",
          buttons: [
            { label: 'No', variant: 'secondary', onClickKey: 'NO' },
            { label: 'Yes', variant: 'primary', onClickKey: 'YES_MOVE_TO_PENDING' },
          ],
        })
      );
    } else if (event.target.textContent.includes("Add") || event.target.textContent.includes("Update")) {
      setShowDateTimePicker(true);
    } else if (event.target.textContent.includes("Cancel")) {
      cancelReminder(task);
    }
  };

  const cancelReminder = async (task) => {
    const payload = {
      accountId: task.accountId,
      staffTaskId: task.staffTaskId,
      dueDate: ''
    };

    try {
      const response = await axios.post('/staffTasks/updateDueDate', payload);
      if (response.data.data) {
        dispatch(setSelectedRows([]));
        dispatch(setSelectAll(false));
        dispatch(setReloadTaskList(true));
        dispatch(
          showAlert({
            header: "Success",
            message: "Successfully Cancelled reminder",
            type: 'success',
          })
        );
      }
    } catch (err) {
      dispatch(
        showAlert({
          header: "Error",
          message: `Could not cancel reminder: ${err}`,
          type: 'danger',
        })
      );
    }
  };

  const fetchAppointmentAndNavigate = async (appointmentId) => {

    if (appointmentId) {
      try {
        const response = await axios.get(`/appointment/patient/${appointmentId}`);
        if (response.data.data) {
          const appointment = response.data.data;
          setAppointment(appointment);
          if (selectedItem.newStatus || selectedItem.newStatus === undefined) {
            onTaskStatusUpdateHandler("NewStatus");
          }
          dispatch(closeModal());
          setShowTranscription(true);
        }
      } catch (err) {
        dispatch(closeModal());
      }
    } else {
      dispatch(closeModal());
    }
  };

  const onClickTaskAction = (event, task) => {
    const taskType = (task?.type || "").toLowerCase();
    dispatch(setSelectedItem(task));
    if (task.newStatus || task.newStatus === undefined) {
      onTaskStatusUpdateHandler("NewStatus", task);
    }
    if (taskType === 'patient vitals' || taskType === 'note review') {
      dispatch(
        openModal({
          activeModalId: "TaskListPanel",
          header: 'Open Patient vital page?',
          message: "Do you want to view the patient vital page? Clicking 'Yes' will allow you to see the vitals that exceeded the threshold?",
          buttons: [
            { label: 'No', variant: 'secondary', onClickKey: 'NO' },
            { label: 'Yes', variant: 'primary', onClickKey: 'YES_PATIENT_VITALS' },
          ],
        })
      );
    } else if (taskType === 'patient submitted report' || taskType === 'patient responded to report') {
      dispatch(
        openModal({
          activeModalId: "TaskListPanel",
          header: 'Open Patient vital page?',
          message: "Do you want to view the new report? Clicking 'Yes' will take you to the patient-submitted reports page.",
          buttons: [
            { label: 'No', variant: 'secondary', onClickKey: 'NO' },
            { label: 'Yes', variant: 'primary', onClickKey: 'YES_PATIENT_REPORT' },
          ],
        })
      );
    } else if (taskType === 'open patient report') {
      dispatch(
        openModal({
          activeModalId: "TaskListPanel",
          header: 'Open Patient vital page?',
          message: "Do you want to view the new report? Clicking 'Yes' will take you to the Patient-submitted Reports page.",
          buttons: [
            { label: 'No', variant: 'secondary', onClickKey: 'NO' },
            { label: 'Yes', variant: 'primary', onClickKey: 'YES_PATIENT_SUBMITTED_REPORT' },
          ],
        })
      );
    } else if (taskType === 'patient discharge' || taskType === 'frequency changed') {
      dispatch(
        openModal({
          activeModalId: "TaskListPanel",
          header: 'Open Patient vital page?',
          message: "Do you want to view the patient vital page? Clicking 'Yes' will allow you to see the vitals that exceeded the threshold?",
          buttons: [
            { label: 'No', variant: 'secondary', onClickKey: 'NO' },
            { label: 'Yes', variant: 'primary', onClickKey: 'YES_CARE_PROGRAM' },
          ],
        })
      );
    } else if (taskType === 'transcription') {
      
      fetchAppointmentAndNavigate(task?.details?.appointmentDetails?.appointmentID)
      dispatch(closeModal());
    } else if (taskType === 'review alarm') {
      dispatch(
        openModal({
          activeModalId: "TaskListPanel",
          header: 'Open Patient alarms page?',
          message: "Do you want to view the patient alarms page? Clicking 'Yes' will allow you to see the patient alarm needing review",
          buttons: [
            { label: 'No', variant: 'secondary', onClickKey: 'NO' },
            { label: 'Yes', variant: 'primary', onClickKey: 'YES_ALARMS' },
          ],
        })
      );
    } else if (taskType === 'ad-hoc recording report') {
      setAdhocDetails(task?.details);
      setShowAdhoc(true);
    }
  };

  const handleDialogButtonClick = (onClickKey) => {
    if (onClickKey === 'YES_PATIENT_VITALS') {
      dispatch(updatePatientInfo(selectedItem?.details?.patientDetails?.patientId));
      dispatch(resetFilterState());
      navigate('/patient-detail');
      dispatch(closeModal());
    } else if (onClickKey === 'YES_PATIENT_REPORT') {
      dispatch(updatePatientInfo(selectedItem?.details?.patientDetails?.patientId));
      dispatch(resetFilterState());
      dispatch(setTaskNonCriticalReportId(selectedItem?.details?.nonCriticalReportDetails?.nonCriticalReportId));
      navigate('/patient-detail/Reports');
      dispatch(closeModal());
    } else if (onClickKey === 'YES_CARE_PROGRAM') {
      dispatch(updatePatientInfo(selectedItem?.details?.patientDetails?.patientId));
      dispatch(resetFilterState());
      dispatch(setTaskEnrollmentId(selectedItem?.details?.enrollmentDetails?.enrollmentId));
      navigate('/patient-detail/CareProgram');
      dispatch(closeModal());
    } else if (onClickKey === 'YES_MOVE_TO_PENDING') {
      onTaskStatusUpdateHandler("Pending");
      dispatch(closeModal());
    } else if (onClickKey === 'YES_MARK_COMPLETE') {
      onTaskStatusUpdateHandler("Completed");
      dispatch(closeModal());
    } else if (onClickKey === 'YES_ARCHIVE') {
      onTaskStatusUpdateHandler("Archived");
      dispatch(closeModal());
    } else if (onClickKey === 'YES_ALARMS') {
      dispatch(updatePatientInfo(selectedItem?.details?.patientDetails?.patientId));
      dispatch(resetFilterState());
      dispatch(setFormField({ key: "taskAlertId", value: selectedItem?.alertId }));
      navigate('/patient-detail/Alarms');
      dispatch(closeModal());
    } else {
      dispatch(closeModal());
    }
  };

  const onTaskStatusUpdateHandler = (taskAction, task) => {
    const urlMap = {
      Completed: '/staffTasks/update/allTaskIds',
      Archived: '/staffTasks/update/allTaskIds',
      Pending: '/staffTasks/update',
      NewStatus: '/staffTasks/update',
    };

    const taskUpdateURL = urlMap[taskAction];
    const tasksToUpdate = selectedRows.length > 0 ? selectedRows : [task];

    if (tasksToUpdate.length >= 1) {
      let taskUpdatePromise;

      if (taskAction === 'Completed' || taskAction === 'Archived') {
        const payload = {
          taskIds: tasksToUpdate.map((task) => task.staffTaskId),
          newStatus: taskAction,
          accountId: tasksToUpdate[0].accountId
        };
        taskUpdatePromise = axios.put(taskUpdateURL, payload);
      } else {
        const payloadPromises = tasksToUpdate.map((task) => {
          const payload = {
            accountId: task.accountId,
            staffTaskId: task.staffTaskId,
            status: taskAction === 'NewStatus' ? task.status : taskAction,
          };
          return axios.put(taskUpdateURL, payload);
        });
        taskUpdatePromise = Promise.all(payloadPromises);
      }

      // Handle responses and errors
      taskUpdatePromise
        .then((responses) => {
          dispatch(setSelectedRows([]));
          dispatch(setSelectAll(false));
          dispatch(closeModal());
          dispatch(setReloadTaskList(true));
          dispatch(
            showAlert({
              header: "Success",
              message: "Successfully Updated Task Status",
              type: 'success',
            })
          );
        })
        .catch((error) => {
          dispatch(closeModal());
          const errorMessage = error.response?.data?.message || 'An error occurred';
          dispatch(
            showAlert({
              header: "Error",
              message: `Could not Update Task Status: ${errorMessage}`,
              type: 'danger',
            })
          );
        });

    }
  };

  const handleCloseDateTimePicker = () => {
    setShowDateTimePicker(false);
  };

  const taskTypeIcon = (task) => {
    if (task.type === "Patient Discharge") {
      return <PatientDischargeTaskIcon  />;
    } else if (task.type === "Note Review") {
      return <NoteReviewTaskIcon />;
    } else if (task.type.toLowerCase() === "patient submitted report" || task.type.toLowerCase() === "patient responded to report") {
      return <PatientReportTaskIcon />;
    } else if (task.type === "Billing") {
      return <BillingTaskIcon  />;
    } else if (task.type === "Transcription") {
      return <TranscriptTaskIcon />;
    } else if (task.type === "Patient Vitals") {
      return <VitalTaskIcon  />;
    } else if (task.type === "Reading Frequency Changed") {
      return <FrequencyChangedTaskIcon  />;
    } else if (task.type === "Device control alarm") {
      return <DeviceControlTaskIcon  />;
    } else {  
      return <VitalTaskIcon />;
    }
  };

  return (
    <div className={taskStyles.TaskListScroll}>
      {filteredTasks.length === 0 &&
        <div className={taskStyles.empty}>
          {`No ${filter?.value.toLowerCase()} tasks found`}
        </div>
      }
      <ListGroup>
        {filteredTasks.map((task, index) => {
          let description = task?.description ?? "";
          if (task.type === "Transcription") {
            const isoRegex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/;
            const match = description.match(isoRegex);
            if (match) {
              const [timestamp] = match;
              const message = description.replace(timestamp, '');
              if (timestamp && message) {
                let formattedDate = formatDateWithTime(timestamp, userDateFormat);
                description = `${message} ${formattedDate}`;
              }
            }
          }
          return (
            <ListGroup.Item
              key={index}
              style={{
                borderBottom: '1px solid #ddd',
                borderTop: '1px solid #ddd',
                padding: '10px',
                marginBottom: '10px',
                borderRadius: '10px',
                backgroundColor: task.checked ? '#f8f9fa' : '#ffffff'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Form.Check className={taskStyles.selectAllFormCheck}
                    type="checkbox"
                    checked={task.checked}
                    onChange={() => handleItemChange(task.staffTaskId)}
                  />
                  <span style={{ display: 'flex', alignItems: 'center', marginLeft: '15px', marginRight: '5px' }}>
                    <div style={{ position: 'relative', width: '24px', height: '24px' }}>
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {taskTypeIcon(task)}
                      </div>


                      {task.newStatus && (
                        <div className={taskStyles.unreadMessageIcon}>
                          <UnreadMessageIconIcon style={{ width: '8px', height: '8px' }} />
                        </div>
                      )}
                    </div>
                  </span>

                  <span style={{ display: 'flex', alignItems: 'center', marginLeft: '5px', marginRight: '10px' }}>
                    <Button
                      variant="link"
                      className={taskStyles.buttonLink}
                      onClick={(event) => onClickTaskAction(event, task)}
                    >
                      {task.type}
                    </Button>
                  </span>
                  |
                  <span className={taskStyles.dateField}>
                    {`Created : ${formatDateWithFullMonthAndTime(task.createdDate, userDateFormat)}`}
                    {(task.completedDate && task.status === "Completed")
                      ? ` | Completed : ${formatDateWithFullMonthAndTime(task.completedDate, userDateFormat)}`
                      : task.dueDate
                        ? ` | Due : ${formatDateWithFullMonthAndTime(task.dueDate, userDateFormat)}`
                        : ''}
                  </span>

                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {(task.isDueDatePassed || task.isDueSoon) && (
                    <OverlayTrigger
                      placement='left'
                      overlay={
                        <Tooltip id={`tooltip-left`}>
                          <strong>{formatDateString(task.dueDate, userDateFormat)}</strong>.
                        </Tooltip>
                      }
                    >
                      <div className={`${taskStyles.ReminderContainer} ${task.isDueDatePassed ? taskStyles.ReminderPastDue : taskStyles.ReminderDueSoon
                        }`}>
                        Reminder
                        <ReminderBellIcon />
                      </div>
                    </OverlayTrigger>
                  )}

                  <DropdownButton
                    variant="link"
                    className="help-icon"
                    title={<span><FontAwesomeIcon icon="fa-solid fa-ellipsis" style={{ color: 'grey', fontSize: '20px' }} /></span>}
                  >
                    {filter?.value === 'Pending' && (<>
                      <Dropdown.Item onClick={(event) => handleItemClick(event, task)}
                        style={{ fontWeight: 'bold', fontSize: '16px' }}
                      >
                        {task.dueDate ? 'Update Reminder' : 'Add Reminder'} <FontAwesomeIcon icon="fa-solid fa-calendar-days" />
                      </Dropdown.Item>
                      {task.dueDate && (<Dropdown.Item
                        onClick={(event) => handleItemClick(event, task)}
                        style={{ fontWeight: 'bold', fontSize: '16px' }}
                      >
                        Cancel Reminder <FontAwesomeIcon icon="fa-solid fa-calendar-days" />
                      </Dropdown.Item>)}</>
                    )}
                    {filter?.value === 'Completed' && (
                      <Dropdown.Item
                        onClick={(event) => handleItemClick(event, task)}
                        style={{ fontWeight: 'bold' }}
                      >
                        Move back to Pending List
                      </Dropdown.Item>
                    )}
                  </DropdownButton>
                </div>

              </div>
              <div className={taskStyles.description} dangerouslySetInnerHTML={{ __html: description }} />

            </ListGroup.Item>
          )
        })
        }
      </ListGroup >
      <PerinHealthModal
        modalId={"TaskListPanel"}
        handleButtonClick={handleDialogButtonClick}
      />
      <AdhocModal
        show={showAdhoc && adhocDetails}
        onHide={() => setShowAdhoc(false)}
        adhocDetails={adhocDetails}
      />
      {showTranscription && (
        <AppointmentTranscription
          show={showTranscription}
          onHide={() => setShowTranscription(false)}
          appointment={appointment}
        />
      )}
      <DateTimePicker show={showDateTimePicker} onClose={handleCloseDateTimePicker} />

    </div >
  );
};

export default TaskListPanel;
